import Nullable from "@/types/utils/Nullable"
import { useCallback, useMemo, useState } from "react"

interface IMaskItem {
    index: number,
    char: string
}

const useMask = (masks: string[]) => {
    const [value, setValue] = useState<Nullable<string>>(null)

    const maskedInput = useMemo((): boolean => masks.length > 0, [])

    /*
        Verifica se existe máscara alfanumérica
        (novo CNPJ)
    */
    const hasAlphaNumericMask = useMemo((): boolean => {
        return masks.some(mask => mask.includes('*'))
    }, [masks])

    /*
        Conta os placeholders da máscara

        Máscaras antigas:
        0 = número

        Novo CNPJ:
        * = alfanumérico
    */
    const getMaskLength = useCallback((mask: string): number => {
        return mask.replace(/[^0*]/g, '').length
    }, [])

    const maxLength = useMemo((): Nullable<number> => {
        if (masks.length === 0) {
            return null
        }

        const maskList = [...masks]
            .map(mask => getMaskLength(mask).toString())
            .sort((a, b) => Number(b) - Number(a))

        return Number(maskList[0])
    }, [masks, getMaskLength])

    const selectedMask = useCallback((text: string): string => {

        /*
            Se digitou letra e existe máscara
            alfanumérica usa ela automaticamente
        */
        if (hasAlphaNumericMask && /[a-z]/i.test(text)) {
            const alphaMask = masks.find(mask => mask.includes('*'))

            if (alphaMask) {
                return alphaMask
            }
        }

        const maskList = [...masks]
            .sort((a, b) => getMaskLength(a) - getMaskLength(b))

        for (let mask of maskList) {
            if (text.length <= getMaskLength(mask)) {
                return mask
            }
        }

        return ''
    }, [masks, getMaskLength, hasAlphaNumericMask])

    const getMaskItems = useCallback((text: string): IMaskItem[] => {

        /*
            Considera:
            0 = placeholder numérico
            * = placeholder alfanumérico

            Todo resto é caractere fixo
        */
        const regex = /[^0*]/g

        let maskItems: IMaskItem[] = []
        let matchFound = true

        while (matchFound) {
            const match = regex.exec(selectedMask(text))

            if (match) {
                maskItems.push({
                    index: match.index,
                    char: match[0]
                })
            }

            matchFound = !!match
        }

        return maskItems
    }, [selectedMask])

    const maskValue = useCallback((text: string): string => {
        if (!maskedInput) {
            const newValue = text.length > 0 ? text : null
            setValue(newValue)
            return newValue ?? ''
        }

        if (hasAlphaNumericMask) {
            text = text
                .replace(/[^A-Z0-9]/gi, '')
                .toUpperCase()
                .replace(/[IOQFU]/g, '')
        } else {
            text = text.replace(/\D/g, '')
        }

        if (maxLength !== null) {
            text = text.slice(0, maxLength)
        }

        const mask = selectedMask(text)

        let result = ''
        let textIndex = 0

        for (let i = 0; i < mask.length; i++) {
            const maskChar = mask[i]
            const currentChar = text[textIndex]

            if (!currentChar) {
                break
            }

            /*
                0 = apenas número
            */
            if (maskChar === '0') {

                if (/\d/.test(currentChar)) {
                    result += currentChar
                    textIndex++
                } else {
                    textIndex++
                    i--
                }
            }

            /*
                * = alfanumérico
            */
            else if (maskChar === '*') {

                /*
                    Letras permitidas no CNPJ Alfa
            
                    NÃO permite:
                    I O Q F U
                */
                if (/[A-HJ-NPR-TV-Z0-9]/i.test(currentChar)) {
                    result += currentChar
                    textIndex++
                } else {
                    textIndex++
                    i--
                }
            }

            /*
                caracteres fixos:
                . / -
            */
            else {
                result += maskChar
            }
        }

        const newValue = result.length > 0 ? result : null

        setValue(newValue)

        return newValue ?? ''
    }, [
        maskedInput,
        maxLength,
        selectedMask,
        hasAlphaNumericMask
    ])

    return { value, maskValue }
}

export default useMask